import React from 'react';
import { fireEvent, render, waitFor } from '@testing-library/react';
import { ConfigProvider } from '@decisiv/ui-components';
import Login from './Login';
import * as services from './services';

const translations = {
  portalLogin: 'Portal Login',
  usernameOrEmail: 'Username or Email',
  password: 'Password',
  forgotPassword: 'Forgot Password',
  logIn: 'Log in',
  legalAgreementsHtml:
    'By logging into the application you agree to the Policies &amp; Agreements.',
  resetPasswordTitle: 'Reset Password',
  resetPasswordInstructions:
    "Enter your account's username, or email address, and we'll send you a link to reset your password.",
  resetPasswordSuccess:
    'Password reset instructions have been sent to your registered email address.',
  sendResetLink: 'Send Link',
  backToLogin: 'Back to Login',
  changePassword: 'Change Password',
  changePasswordInstructions: 'Enter a new password to complete the reset.',
  passwordRequirements: 'Password Requirements:',
  passwordRequirementLength:
    'At least 8 characters — the more characters, the better.',
  passwordRequirementCase: 'A mixture of both uppercase and lowercase letters.',
  passwordRequirementAlphanumeric: 'A mixture of letters and numbers.',
  passwordsDoNotMatch: 'The passwords do not match!',
  checkYourEmailTitle: 'Check your email',
  checkYourEmailInstructions: 'We have sent you an email with a link.',
};

const props = {
  csrfToken: '123abc',
  dealerId: '12345678',
  translations,
};

jest.mock('./services');

describe('Login', () => {
  describe('Validations', () => {
    it('renders correctly', () => {
      const { getByText, getByLabelText } = render(<Login {...props} />);
      expect(getByText('Portal Login')).toBeInTheDocument();
      expect(getByLabelText('Username or Email')).toBeInTheDocument();
    });

    it('disables login button when identity is empty', () => {
      const { getByRole } = render(<Login {...props} />);
      const loginButton = getByRole('button', { name: 'Log in' });
      expect(loginButton).toBeDisabled();
    });

    it('enables login button when identity is provided', () => {
      const { getByRole, getByLabelText } = render(<Login {...props} />);
      const identityField = getByLabelText('Username or Email');
      fireEvent.change(identityField, { target: { value: 'testuser' } });
      const loginButton = getByRole('button', { name: 'Log in' });
      expect(loginButton).toBeEnabled();
    });

    it('shows password field if postCheckSso returns allow_password_login true', async () => {
      services.postCheckUsernameSso.mockResolvedValueOnce({
        json: async () => ({ allow_password_login: true }),
      });

      const { getByLabelText, queryByLabelText, getByRole } = render(
        <Login {...props} />,
      );
      const identityField = getByLabelText('Username or Email');
      fireEvent.change(identityField, { target: { value: 'testuser' } });

      const loginButton = getByRole('button', { name: 'Log in' });
      fireEvent.click(loginButton);

      await waitFor(() => {
        expect(queryByLabelText('Password')).toBeInTheDocument();
      });

      expect(getByRole('button', { name: 'Log in' })).toBeDisabled();
    });

    it('shows the check-your-email screen when an invitation was just sent', async () => {
      services.postCheckUsernameSso.mockResolvedValueOnce({
        json: async () => ({
          requires_sso: false,
          invitation_sent: true,
          provider: 'pcaraz',
        }),
      });

      delete window.location;
      window.location = { href: '' };

      const { getByLabelText, getByRole, getByText } = render(
        <Login {...props} />,
      );
      fireEvent.change(getByLabelText('Username or Email'), {
        target: { value: 'fleet@example.com' },
      });
      fireEvent.click(getByRole('button', { name: 'Log in' }));

      await waitFor(() => expect(getByText('Check your email')).toBeTruthy());

      expect(getByText('We have sent you an email with a link.')).toBeTruthy();
      expect(window.location.href).toEqual('');
    });

    it('returns to the login screen from the check-your-email screen', async () => {
      services.postCheckUsernameSso.mockResolvedValueOnce({
        json: async () => ({
          requires_sso: false,
          invitation_sent: true,
          provider: 'pcaraz',
        }),
      });

      delete window.location;
      window.location = { href: '' };

      const { getByLabelText, getByRole, getByText } = render(
        <Login {...props} />,
      );
      fireEvent.change(getByLabelText('Username or Email'), {
        target: { value: 'fleet@example.com' },
      });
      fireEvent.click(getByRole('button', { name: 'Log in' }));

      await waitFor(() => expect(getByText('Check your email')).toBeTruthy());
      fireEvent.click(getByRole('button', { name: 'Back to Login' }));

      await waitFor(() => expect(getByText('Portal Login')).toBeTruthy());
    });

    it('redirects to redirect_url if postCheckSso returns requires_sso true', async () => {
      const mockRedirectUrl = '/sso-redirect';
      services.postCheckUsernameSso.mockResolvedValueOnce({
        json: async () => ({
          requires_sso: true,
          redirect_url: mockRedirectUrl,
        }),
      });

      delete window.location;
      window.location = { href: '' };

      const { getByLabelText, getByRole } = render(<Login {...props} />);
      const identityField = getByLabelText('Username or Email');
      fireEvent.change(identityField, { target: { value: 'testuser' } });

      const loginButton = getByRole('button', { name: 'Log in' });
      fireEvent.click(loginButton);

      await waitFor(() => {
        expect(window.location.href).toBe(mockRedirectUrl);
      });
    });

    it('submits form when Enter key is pressed on username field', async () => {
      services.postCheckUsernameSso.mockResolvedValueOnce({
        json: async () => ({ allow_password_login: true }),
      });

      const { getByLabelText } = render(<Login {...props} />);
      const identityField = getByLabelText('Username or Email');
      fireEvent.change(identityField, { target: { value: 'testuser' } });

      // Press Enter on the username field
      fireEvent.keyPress(identityField, {
        key: 'Enter',
        code: 'Enter',
        charCode: 13,
      });

      await waitFor(() => {
        expect(services.postCheckUsernameSso).toHaveBeenCalledWith(
          props.csrfToken,
          'testuser',
        );
      });
    });
  });
});

describe('Request password', () => {
  it('disables request button if form is invalid', async () => {
    const { getByRole } = render(
      <Login defaultScreen="PasswordReset" {...props} />,
    );

    const sendButton = getByRole('button', { name: 'Send Link' });
    expect(sendButton).toBeDisabled();
  });

  it('enables request button if form is valid', async () => {
    const { getByRole, getByLabelText } = render(
      <Login defaultScreen="PasswordReset" {...props} />,
    );

    const identityField = getByLabelText('Username or Email');

    const sendButton = getByRole('button', { name: 'Send Link' });

    fireEvent.change(identityField, { target: { value: 'username' } });

    expect(sendButton).toBeEnabled();
  });

  it('shows the translated success notification after requesting a reset', async () => {
    services.postPasswordResetService.mockResolvedValue({
      json: async () => ({}),
    });

    const { getByRole, getByLabelText, findByText } = render(
      <ConfigProvider>
        <Login defaultScreen="PasswordReset" {...props} />
      </ConfigProvider>,
    );

    fireEvent.change(getByLabelText('Username or Email'), {
      target: { value: 'username' },
    });
    fireEvent.click(getByRole('button', { name: 'Send Link' }));

    expect(
      await findByText(translations.resetPasswordSuccess),
    ).toBeInTheDocument();
  });
});
